/* jshint esversion: 10 */
/**
 * Shared utilities for ReviewSummary components
 * Used by both ReviewSummary.jsx and ViewFormSummary.jsx
 */

/**
 * Formats a date string to display format: "Month DD, YYYY"
 * Handles various input formats:
 * - "Fri Jun 19 2026" -> "June 19, 2026"
 * - "2026-06-19" -> "June 19, 2026"
 * - ISO date strings -> "June 19, 2026"
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or original value if invalid
 */
export function formatReviewDate(dateString) {
  if (!dateString || dateString === "-") return dateString;
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    // Format as: Month DD, YYYY (e.g., "June 19, 2026")
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch (error) {
    console.warn("Invalid date format:", dateString);
    return dateString;
  }
}

/**
 * Determines if a search query is valid for searching
 * - Numeric queries can be any length
 * - Alphabetic queries must be at least 3 characters (excluding special chars)
 */
export function canSearch(query) {
  if (!query) return false;
  // Allow immediate search for numeric input
  if (/\d/.test(query)) return true;
  // Require at least 3 alphabetic characters
  return query.replace(/[^a-z]/gi, "").length >= 3;
}

/**
 * Creates a file download mutation configuration for react-query
 * @param {Object} FilesUploadService - The file upload service
 * @returns {Object} Mutation configuration object
 */
export function createFileDownloadMutation(FilesUploadService) {
  return {
    mutationFn: async (fileName) => {
      return await FilesUploadService.downloadFile(fileName);
    },
    onSuccess: (data, fileName) => {
      // data.data is already a Blob (responseType: 'blob') — use it directly
      // to preserve the original MIME type from server headers.
      const blob = data.data instanceof Blob
        ? data.data
        : new Blob([data.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Extract filename from the path
      const fileNameOnly = fileName.split('/').pop() || 'download';
      link.setAttribute('download', fileNameOnly);

      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (e) => {
      console.log("File download error:", e);
    },
  };
}

/**
 * Builds a searchable dataset from form data and accordion sections
 * Creates a flattened structure with section IDs and searchable text
 */
export function buildSearchableDataset(formData, accordionSections) {
  return accordionSections.map(({ sectionId, summary }) => ({
    sectionId,
    searchableText: [summary, collectSearchableValues(formData?.[sectionId])]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
  }));
}

/**
 * Recursively collects all searchable values from nested data structures
 * Handles objects, arrays, and JSON strings
 */
export function collectSearchableValues(value, path = []) {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value
      .map((item, index) => collectSearchableValues(item, [...path, index]))
      .join(" ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, childValue]) =>
        [key, collectSearchableValues(childValue, [...path, key])].join(" "),
      )
      .join(" ");
  }

  const stringValue = String(value);
  const parsedValue = parseJsonString(stringValue);
  if (parsedValue) {
    return [
      path[path.length - 1],
      stringValue,
      collectSearchableValues(parsedValue),
    ]
      .filter(Boolean)
      .join(" ");
  }

  return [path[path.length - 1], stringValue].filter(Boolean).join(" ");
}

/**
 * Attempts to parse a JSON string
 * Returns null if the string is not valid JSON
 */
export function parseJsonString(value) {
  const trimmedValue = value.trim();
  if (!trimmedValue || !["{", "["].includes(trimmedValue[0])) return null;

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return null;
  }
}

/**
 * Removes all search highlights and section match classes from container
 */
export function clearSearchHighlights(container) {
  container
    .querySelectorAll(".review-search-section-match")
    .forEach((element) => {
      element.classList.remove("review-search-section-match");
    });

  container.querySelectorAll("mark.review-search-highlight").forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;

    parent.replaceChild(document.createTextNode(mark.textContent), mark);
    parent.normalize();
  });
}

/**
 * Highlights all matching text within accordion elements
 * Returns array of highlighted elements for scrolling/focusing
 */
export function highlightMatchingElements(accordions, query) {
  const highlightedElements = [];

  accordions.forEach((accordion) => {
    const textNodes = getMatchingTextNodes(accordion, query);

    textNodes.forEach((textNode) => {
      highlightedElements.push(...wrapTextNodeMatches(textNode, query));
    });
  });

  return highlightedElements;
}

/**
 * Finds all text nodes within an accordion that match the search query
 * Excludes icon, button, script, style, and mark elements
 */
export function getMatchingTextNodes(accordion, query) {
  const textNodes = [];
  const walker = document.createTreeWalker(
    accordion,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        const parentElement = node.parentElement;
        if (
          parentElement?.closest(
            "i2c-icon, i2c-icon-button, button, script, style, mark",
          )
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue.toLowerCase().includes(query)) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  return textNodes;
}

/**
 * Wraps matching portions of a text node with <mark> elements for highlighting
 * Returns array of created mark elements
 */
export function wrapTextNodeMatches(textNode, query) {
  const text = textNode.nodeValue;
  const lowerText = text.toLowerCase();
  const fragment = document.createDocumentFragment();
  const highlightedElements = [];
  let cursor = 0;
  let matchIndex = lowerText.indexOf(query);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      fragment.appendChild(
        document.createTextNode(text.slice(cursor, matchIndex)),
      );
    }

    const mark = document.createElement("mark");
    mark.className = "review-search-highlight";
    mark.textContent = text.slice(matchIndex, matchIndex + query.length);
    fragment.appendChild(mark);
    highlightedElements.push(mark);

    cursor = matchIndex + query.length;
    matchIndex = lowerText.indexOf(query, cursor);
  }

  if (cursor < text.length) {
    fragment.appendChild(document.createTextNode(text.slice(cursor)));
  }

  textNode.parentNode.replaceChild(fragment, textNode);
  return highlightedElements;
}

/**
 * Opens all ancestor accordions of the given element
 * Useful for ensuring nested content is visible
 */
export function openAncestorAccordions(element) {
  let currentAccordion = element?.closest?.("i2c-accordion");

  while (currentAccordion) {
    currentAccordion.open = true;
    currentAccordion = currentAccordion.parentElement?.closest("i2c-accordion");
  }
}
